import type { User, Division } from '../../UserDetail/types';
import type { SrmData } from '../types';

export interface DivisionWithPotentialBid extends Division {
  potentialBid: {
    maxBid: number | null;
    isDiscountBidding: boolean;
    enabled: boolean;
  };
  simulation?: {
    maxBid: number;
    discount: number;
    discountPercent: number;
    discountedAmount: number;
  };
}

export interface TemplateHelpers {
  buyerList: string;
  loginLink: string;
  supplierPortalUrl: string;
  eligibleInvoiceCount: number;
  eligibleInvoiceAmount: string;
  discount: string;
  discountLo: string;
  discountHi: string;
  discountedAmount: string;
  discountedAmountLo: string;
  discountedAmountHi: string;
  discountPercent: string;
  eligibleDpeWeightedAvg: number;
  dpe: number;
  blendedReadableBid: string;
  blendedReadableBidLo: string;
  blendedReadableBidHi: string;
}

export interface SimulationHelpers {
  [key: string]: string | number | null;
}

const DISCOUNT_OFFSET = 0.005;
const APR_OFFSET = 0.02;

function simpleTemplate(templateString: string, data: Record<string, unknown>): string {
  return templateString.replace(/\{\{([\s\S]+?)\}\}/g, (match, expression) => {
    const trimmed = expression.trim();
    try {
      const value = trimmed.split('.').reduce((obj: unknown, key: string) => {
        if (obj && typeof obj === 'object' && key in obj) {
          return (obj as Record<string, unknown>)[key];
        }

        return undefined;
      }, data);
      return value != null ? String(value) : '';
    } catch {
      return '';
    }
  });
}

/**
 * Renders an email template by replacing mustache-style placeholders ({{variable}}) with actual values
 * Supports nested property access (e.g., {{user.firstName}})
 * @param user - User object for template context
 * @param templateString - Template string with {{placeholders}}
 * @param srm - SRM data for template context
 * @param simulationHelpers - Array of simulation helpers for individual offers
 * @param helpers - Template helpers with aggregated data (buyer list, amounts, etc.)
 * @returns Rendered template string with placeholders replaced
 */
export function renderTemplate(
  user: User | null,
  templateString: string | null | undefined,
  srm: SrmData | null,
  simulationHelpers: SimulationHelpers[],
  helpers: TemplateHelpers,
): string {
  if (!templateString) return '';

  return simpleTemplate(templateString, {
    user: user ?? {},
    srm: srm ?? {},
    offers: simulationHelpers,
    helpers,
  });
}

/**
 * Builds template helper object with aggregated data for email templates
 * Calculates totals, discounts, amounts, and other aggregated metrics across all enabled opportunities
 * Handles currency conversion using exchange rates
 * @param opportunities - Array of divisions with potential bids and simulations
 * @param currency - Target currency for amounts
 * @param exchangeRates - Exchange rates object for currency conversion
 * @returns Template helpers object with aggregated data for use in templates
 */
export function buildTemplateHelpers(
  opportunities: DivisionWithPotentialBid[],
  currency: string,
  exchangeRates: Record<string, number> | null,
): TemplateHelpers {
  const enabledOpportunities = opportunities.filter((d) => d.simulation);

  if (enabledOpportunities.length === 0) {
    return {
      buyerList: '',
      loginLink: '',
      supplierPortalUrl: '',
      eligibleInvoiceCount: 0,
      eligibleInvoiceAmount: formatCurrency(0, currency),
      discount: formatCurrency(0, currency),
      discountLo: formatCurrency(0, currency),
      discountHi: formatCurrency(0, currency),
      discountedAmount: formatCurrency(0, currency),
      discountedAmountLo: formatCurrency(0, currency),
      discountedAmountHi: formatCurrency(0, currency),
      discountPercent: '0%',
      eligibleDpeWeightedAvg: 0,
      dpe: 0,
      blendedReadableBid: '0%',
      blendedReadableBidLo: '0%',
      blendedReadableBidHi: '0%',
    };
  }

  const convertAmount = (amount: number, fromCurrency: string): number => {
    if (!exchangeRates || fromCurrency === currency) return amount;
    const fromRate = exchangeRates[fromCurrency] ?? 1;
    const toRate = exchangeRates[currency] ?? 1;
    return amount * (fromRate / toRate);
  };

  const totalEligibleAmount = enabledOpportunities.reduce(
    (sum, d) => sum + convertAmount(d.eligibleInvoiceAmount || 0, d.currency || 'USD'),
    0,
  );
  const totalDiscount = enabledOpportunities.reduce(
    (sum, d) => sum + convertAmount(d.simulation?.discount || 0, d.currency || 'USD'),
    0,
  );
  const totalDiscountedAmount = enabledOpportunities.reduce(
    (sum, d) => sum + convertAmount(d.simulation?.discountedAmount || 0, d.currency || 'USD'),
    0,
  );
  const totalDpeWeightedSum = enabledOpportunities.reduce((sum, d) => sum + (d.eligibleDpeWeightedSum || 0), 0);

  const buyerNames = Array.from(new Set(enabledOpportunities.map((d) => d.makerDivisionName || '').filter(Boolean)));
  const buyerList =
    buyerNames.length > 1
      ? buyerNames.slice(0, -1).join(', ') + ' and ' + buyerNames[buyerNames.length - 1]
      : buyerNames[0] || '';

  const blendedDiscount = totalEligibleAmount > 0 ? totalDiscount / totalEligibleAmount : 0;
  const dpe = totalEligibleAmount > 0 ? totalDpeWeightedSum / totalEligibleAmount : 0;
  const blendedReadableBid = dpe > 0 ? blendedDiscount * (360 / dpe) * 100 : 0;

  const firstOpp = enabledOpportunities[0];
  const supplierPortalUrl = firstOpp?.makerOrganizationUuid ? `https://c2fo.com/${firstOpp.makerOrganizationUuid}` : '';

  return {
    buyerList,
    loginLink: '',
    supplierPortalUrl,
    eligibleInvoiceCount: Math.ceil(enabledOpportunities.reduce((sum, d) => sum + (d.eligibleInvoiceCount || 0), 0)),
    eligibleInvoiceAmount: formatCurrency(totalEligibleAmount, currency),
    discount: formatCurrency(totalDiscount, currency),
    discountLo: formatCurrency(
      enabledOpportunities.reduce((sum, d) => {
        const discountPercent = (d.simulation?.discountPercent || 0) - DISCOUNT_OFFSET;
        const eligibleAmount = convertAmount(d.eligibleInvoiceAmount || 0, d.currency || 'USD');
        return sum + eligibleAmount * Math.max(0, discountPercent);
      }, 0),
      currency,
    ),
    discountHi: formatCurrency(
      enabledOpportunities.reduce((sum, d) => {
        const discountPercent = (d.simulation?.discountPercent || 0) + DISCOUNT_OFFSET;
        const eligibleAmount = convertAmount(d.eligibleInvoiceAmount || 0, d.currency || 'USD');
        return sum + eligibleAmount * discountPercent;
      }, 0),
      currency,
    ),
    discountedAmount: formatCurrency(totalDiscountedAmount, currency),
    discountedAmountLo: formatCurrency(
      enabledOpportunities.reduce((sum, d) => {
        const discountPercent = (d.simulation?.discountPercent || 0) + DISCOUNT_OFFSET;
        const eligibleAmount = convertAmount(d.eligibleInvoiceAmount || 0, d.currency || 'USD');
        return sum + eligibleAmount * (1 - discountPercent);
      }, 0),
      currency,
    ),
    discountedAmountHi: formatCurrency(
      enabledOpportunities.reduce((sum, d) => {
        const discountPercent = (d.simulation?.discountPercent || 0) - DISCOUNT_OFFSET;
        const eligibleAmount = convertAmount(d.eligibleInvoiceAmount || 0, d.currency || 'USD');
        return sum + eligibleAmount * (1 - Math.max(0, discountPercent));
      }, 0),
      currency,
    ),
    discountPercent: formatPercent(blendedDiscount * 100, 2),
    eligibleDpeWeightedAvg: Math.ceil(dpe),
    dpe: Math.round(dpe),
    blendedReadableBid: formatPercent(blendedReadableBid, 2),
    blendedReadableBidLo: formatPercent(Math.max(0, blendedReadableBid - APR_OFFSET * 100), 2),
    blendedReadableBidHi: formatPercent(blendedReadableBid + APR_OFFSET * 100, 2),
  };
}

/**
 * Extracts simulation helper data for a single opportunity/division
 * Provides individual offer data for use in template table rendering
 * Handles currency conversion and formats values as strings for template use
 * @param opportunity - Division with potential bid and simulation data
 * @param selectedCurrency - Target currency for amounts
 * @param exchangeRates - Exchange rates object for currency conversion
 * @returns Simulation helpers object with individual offer data
 */
export function getSimulationHelpers(
  opportunity: DivisionWithPotentialBid,
  selectedCurrency: string,
  exchangeRates: Record<string, number> | null,
): SimulationHelpers {
  const exposed: SimulationHelpers = {
    makerDivisionName: String(opportunity.makerDivisionName || ''),
    takerDivisionName: String(opportunity.name || ''),
    buyerSupplierId: String(opportunity.buyerSupplierId || ''),
    takerId: String(opportunity.takerId || ''),
    marketId: String(opportunity.marketId || ''),
    takerUuid: String(opportunity.takerUuid || ''),
    marketUuid: String(opportunity.marketUuid || ''),
    eligibleInvoiceCount: String(opportunity.eligibleInvoiceCount || 0),
    takerConfigIsEnabled: String(opportunity.takerConfigIsEnabled || false),
    takerConfigIsDiscountBidding: String(opportunity.takerConfigIsDiscountBidding || false),
    takerConfigUpdated: String(opportunity.takerConfigUpdated || ''),
    makerOrganizationName: String(opportunity.makerOrganizationName || ''),
    makerOrganizationUuid: String(opportunity.makerOrganizationUuid || ''),
  };

  if (!opportunity.simulation) {
    return exposed;
  }

  const convertAmount = (amount: number, fromCurrency: string): number => {
    if (!exchangeRates || fromCurrency === selectedCurrency) return amount;
    const fromRate = exchangeRates[fromCurrency] ?? 1;
    const toRate = exchangeRates[selectedCurrency] ?? 1;
    return amount * (fromRate / toRate);
  };

  const divisionCurrency = opportunity.currency || 'USD';
  const sim = opportunity.simulation;
  const currentOffer = opportunity.takerConfigIsDiscountBidding
    ? opportunity.takerConfigMaxDiscount || 0
    : opportunity.takerConfigMaxApr || 0;
  const currentOfferType = opportunity.takerConfigIsDiscountBidding ? 'DISC' : 'APR';
  const suggestedOfferType = opportunity.potentialBid.isDiscountBidding ? 'DISC' : 'APR';

  const suggestedDiscountPercent = sim.discountPercent;
  const suggestedDiscountPercentLo = Math.max(0, suggestedDiscountPercent - DISCOUNT_OFFSET);
  const suggestedDiscountPercentHi = suggestedDiscountPercent + DISCOUNT_OFFSET;

  const offsetFactor = opportunity.potentialBid.isDiscountBidding ? DISCOUNT_OFFSET : APR_OFFSET;
  const suggestedBidLo = Math.max(0.01, sim.maxBid - offsetFactor);
  const suggestedBidHi = sim.maxBid + offsetFactor;

  exposed.suggestedDiscountPercent = String(suggestedDiscountPercent);
  exposed.suggestedDiscountPercentLo = String(suggestedDiscountPercentLo);
  exposed.suggestedDiscountPercentHi = String(suggestedDiscountPercentHi);
  exposed.readableBid = `${formatPercent(sim.maxBid * 100, 2)} ${suggestedOfferType}`;
  exposed.readableBidLo = `${formatPercent(suggestedBidLo * 100, 2)} ${suggestedOfferType}`;
  exposed.readableBidHi = `${formatPercent(suggestedBidHi * 100, 2)} ${suggestedOfferType}`;
  exposed.currentReadableBid = `${formatPercent(currentOffer, 2)} ${currentOfferType}`;
  exposed.eligibleDpeWeightedAvg = String(
    Math.ceil(opportunity.eligibleDpeWeightedSum / (opportunity.eligibleInvoiceAmount || 1)),
  );
  exposed.discountPercent = formatPercent(suggestedDiscountPercent * 100, 2);
  exposed.discountPercentLo = formatPercent(suggestedDiscountPercentLo * 100, 2);
  exposed.discountPercentHi = formatPercent(suggestedDiscountPercentHi * 100, 2);
  const totalAmount = convertAmount(sim.discountedAmount + sim.discount, divisionCurrency);
  const discountAmount = convertAmount(sim.discount, divisionCurrency);
  const discountedAmount = convertAmount(sim.discountedAmount, divisionCurrency);

  exposed.eligibleInvoiceAmount = formatCurrency(totalAmount, selectedCurrency);
  exposed.discount = formatCurrency(discountAmount, selectedCurrency);
  exposed.discountLo = formatCurrency(totalAmount * suggestedDiscountPercentLo, selectedCurrency);
  exposed.discountHi = formatCurrency(totalAmount * suggestedDiscountPercentHi, selectedCurrency);
  exposed.discountedAmount = formatCurrency(discountedAmount, selectedCurrency);
  exposed.discountedAmountLo = formatCurrency(totalAmount * (1 - suggestedDiscountPercentHi), selectedCurrency);
  exposed.discountedAmountHi = formatCurrency(totalAmount * (1 - suggestedDiscountPercentLo), selectedCurrency);
  exposed.payDate = opportunity.payDate ? opportunity.payDate.split('T')[0] : null;
  exposed.lastPlayDate = opportunity.lastMakerMarketClearDate
    ? opportunity.lastMakerMarketClearDate.split('T')[0] || opportunity.lastMakerMarketClearDate
    : null;

  return exposed;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatPercent(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)}%`;
}
